import React, { useEffect, useState, useRef } from "react";
import OtherPageHeader from "../../components/OtherHeader";
import { useForm, Controller } from "react-hook-form";
import { Form, Input, Button, message, Row, Col, Card } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import AxiosInstance from "../../AxiosInstance";
import "./EmailValidation.css";
import { getIpAddress } from "../../utils/getIpAddress";

// Static consents shown on UI
const STATIC_CONSENTS = [
  {
    key: "privacy_terms",
    isMandatory: true,
    matchText: "Privacy Policy and Terms",
    message: (
      <>
        I have read, understood, and hereby accept the{" "}
        <a
          href="https://www.thepolicyexchange.com/privacy-policy"
          target="_blank"
          rel="noreferrer"
          style={{ color: "inherit", textDecoration: "underline" }}
        >
          Privacy Policy
        </a>{" "}
        and{" "}
        <a
          href="https://www.thepolicyexchange.com/terms"
          target="_blank"
          rel="noreferrer"
          style={{ color: "inherit", textDecoration: "underline" }}
        >
          Terms & Conditions
        </a>
      </>
    ),
  },
  {
    key: "communications",
    isMandatory: false,
    matchText: "communications",
    message: "I agree to receive communications of The Policy Exchange.",
  },
];

// Fetch from API only to resolve consentIds at submit time
const getEmailConsentsFromApi = async () => {
  try {
    const response = await AxiosInstance().get("/loan-consent/filter", {
      params: { screenName: "Email", isActive: true },
    });
    if (response.data?.success) return response.data.data;
    return [];
  } catch (error) {
    return [];
  }
};

// Fetch user's existing consent statuses by mobile
const getUserConsentsByMobile = async (mobileNumber) => {
  try {
    const response = await AxiosInstance().get("/loan-consent/consents-by-mobile", {
      params: { mobileNumber },
    });
    if (response.data?.success) return response.data.data.consents;
    return [];
  } catch (error) {
    return [];
  }
};

export default function EmailValidation({
  loanId: propLoanId,
  loanStepId: propLoanStepId,
  stepId: propStepId,
  embedded = false,
  onSuccess,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const loanId = propLoanId || location.state?.loanId;
  const loanStepId = propLoanStepId || location.state?.loanStepId;
  const stepId = propStepId || location.state?.stepId;

  const [otpSent, setOtpSent] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loanData, setLoanData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [currentEmail, setCurrentEmail] = useState("");

  const [otp, setOtp] = useState(Array(4).fill(""));
  const otpInputRefs = useRef([]);
  const [showConsentMsg, setShowConsentMsg] = useState(false);
  // consentStates keyed by STATIC_CONSENTS key (e.g. "privacy_terms", "communications")
  const [consentStates, setConsentStates] = useState({});

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: { email: "" },
  });

  /* ================= API CALLS ================= */

  useEffect(() => {
    if (!loanId) {
      message.warning("Invalid loan. Redirecting to dashboard...");
      navigate("/phdashboard");
      return;
    }
    getUserMe();
    fetchLoanData();
  }, [loanId]);

  const fetchLoanData = async () => {
    try {
      const resp = await AxiosInstance().get(`/policy/loan/${loanId}`);
      setLoanData(resp.data);
    } catch {
      message.error("Failed to load loan details");
    }
  };

  const getUserMe = async () => {
    const resp = await AxiosInstance().get("/user/me");
    setUserData(resp.data);
    setValue("email", resp.data.email || "");
    
    // Prefill consent states from user's existing consents if mobile is available
    if (resp.data.phoneNumber && resp.data.phoneNumber.length === 10) {
      const userConsents = await getUserConsentsByMobile(resp.data.phoneNumber);
      // Map API consentId back to static key via matchText
      const apiConsents = await getEmailConsentsFromApi();
      const newStates = {};
      STATIC_CONSENTS.forEach((staticConsent) => {
        const apiMatch = apiConsents.find((c) =>
          c.consentMessage?.includes(staticConsent.matchText) && c.screenName === "Email"
        );
        if (apiMatch) {
          const userMatch = userConsents.find((uc) => 
            uc.consentId === apiMatch.consentId && 
            (uc.loanId === parseInt(loanId) || uc.loanId === null)
          );
          if (userMatch) newStates[staticConsent.key] = userMatch.consentStatus;
        }
      });
      setConsentStates(newStates);
    }
  };

  const handleSendOtp = async (data) => {
    try {
      setLoading(true);
      
      // Send OTP request matching your backend API
      const resp = await AxiosInstance().post("/tpe-service/email/otp", {
        email: data.email,
        policyNumber: loanData?.policy?.policyNumber,
      });

      // Check if OTP was sent successfully
      if (resp.data.success && resp.data.statusCode === 200) {
        setOtpSent(true);
        setCurrentEmail(data.email); // Store current email
        message.success(resp.data.message || "OTP sent to your email");
        
        // Check if all mandatory consents are accepted
        if (!areMandatoryConsentsAccepted()) {
          message.error("Please accept all mandatory consents");
          setLoading(false);
          return;
        }

        // Start resend timer (30 seconds)
        setResendTimer(30);
        const timer = setInterval(() => {
          setResendTimer((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        message.error(resp.data.message || "Failed to send OTP");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          "Failed to send OTP";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!currentEmail || resendTimer > 0) return;
    
    try {
      setLoading(true);
      
      const resp = await AxiosInstance().post("/tpe-service/email/otp", {
        email: currentEmail,
        policyNumber: loanData?.policy?.policyNumber,
      });

      if (resp.data.success && resp.data.statusCode === 200) {
        message.success(resp.data.message || "OTP resent to your email");
        
        // Reset OTP inputs and restart timer
        setOtp(Array(4).fill(""));
        setResendTimer(30);
        const timer = setInterval(() => {
          setResendTimer((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        
        // Focus first OTP input
        setTimeout(() => {
          otpInputRefs.current[0]?.focus();
        }, 100);
      } else {
        message.error(resp.data.message || "Failed to resend OTP");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          "Failed to resend OTP";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (data) => {
    try {
      setLoading(true);
      const otpCode = otp.join("");

      // Verify OTP request matching your backend API
      const resp = await AxiosInstance().post(
        "/tpe-service/email/otp/verify",
        {
          email: data.email,
          policyNumber: loanData?.policy?.policyNumber,
          otp: Number(otpCode),
        }
      );

      // Check if OTP was verified successfully
      if (resp.data.success && resp.data.statusCode === 200) {
        // Submit consent data after successful OTP verification
        await submitConsentData(userData);
        
        // For modal usage, just show success and call onSuccess callback
        if (embedded) {
          if (onSuccess) {
            onSuccess();
          }
        } else {
          // For standalone page, navigate to loan track
          navigate(`/phdashboard/loan/${loanId}`);
        }
      } else {
        // Handle specific error messages from backend
        const errorMessage = resp.data?.message || 
                          resp.data?.error || 
                          "OTP verification failed";
        message.error(errorMessage);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          "OTP verification failed";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (data) => {
    otpSent ? handleVerifyOtp(data) : handleSendOtp(data);
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);

    // Auto-focus next input
    if (value && index < 3) {
      otpInputRefs.current[index + 1]?.focus();
    }
    
    // Auto-focus previous input on backspace
    if (!value && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    
    // Check if pasted data is a 4-digit number
    if (/^\d{4}$/.test(pastedData)) {
      const otpArray = pastedData.split('');
      setOtp(otpArray);
      
      // Focus the last input after paste
      setTimeout(() => {
        otpInputRefs.current[3]?.focus();
      }, 100);
    }
  };

  // Fetch API consents to get IDs, match with static consents, then submit/update
  const submitConsentData = async (userData) => {
    try {
      // Validate user data
      if (!userData || !userData.userId) {
        message.error("User data is incomplete. Please try again.");
        return;
      }
      const ipAddress = await getIpAddress();
      // Fetch policy details from LoanAgainstPolicy table using loanId
      let policyNumber = null;
      let policyId = null;
      try {
        const policyResponse = await AxiosInstance().get(`/loan-consent/by-loan/${loanId}`);
        if (policyResponse.data?.success && policyResponse.data.data) {
          policyNumber = policyResponse.data.data.policyNumber;
          policyId = policyResponse.data.data.policyId;
        }
      } catch (error) {
        console.error('EmailValidation error fetching policy details:', error);
      }
      
      const apiConsents = await getEmailConsentsFromApi();
      
      // Get existing user consents to check if we need to update or create
      const existingUserConsents = await getUserConsentsByMobile(userData.phoneNumber);
      
      const consentTransactions = STATIC_CONSENTS.map((staticConsent) => {
        const apiMatch = apiConsents.find((c) =>
          c.consentMessage?.includes(staticConsent.matchText) && c.screenName === "Email"
        );
        
        if (!apiMatch) return null;
        
        // Check if this consent already exists for the user (either loan-specific or global)
        const existingConsent = existingUserConsents.find(uc => 
          uc.consentId === apiMatch.consentId && 
          (uc.loanId === parseInt(loanId) || uc.loanId === null)
        );
        
        const consentData = {
          consentId: apiMatch.consentId,
          consentStatus: consentStates[staticConsent.key] || false,
          isActive: true,
          userId: userData.userId,
          loanId: parseInt(loanId),
          policyNumber: policyNumber,
          policyId: policyId,
          mobileNumber: userData.phoneNumber,
          ipAddress:ipAddress
        };
        
        return {
          ...consentData,
          existingTransaction: existingConsent, // Will be used to determine create vs update
        };
      }).filter((t) => t && t.consentId !== null); // skip unmatched
      
      for (const consentTx of consentTransactions) {
        try {
          if (consentTx.existingTransaction) {
            // Update existing transaction using PUT endpoint
            const updateData = {
              consentStatus: consentTx.consentStatus,
              ipAddress:ipAddress
            };
            await AxiosInstance().put(`/loan-consent/transaction/${consentTx.existingTransaction.loanConsentId}`, updateData);
          } else {
            // Create new transaction using POST endpoint
            const createData = {
              ...consentTx,
              createdBy: String(userData.userId),
              UpdatedBy: null,
              ipAddress:ipAddress
            };
            delete createData.existingTransaction; // Remove helper property
            await AxiosInstance().post("/loan-consent/transaction", createData);
          }
        } catch (error) {
          // Error handling without console logging
        }
      }
    } catch (error) {
      // Error handling without console logging
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
    
    // Handle enter key for verification
    if (e.key === 'Enter' && otp.every(digit => digit !== '')) {
      const formData = { email: currentEmail };
      handleVerifyOtp(formData);
    }
  };

  const handleConsentChange = (key, checked) => {
    setConsentStates((prev) => ({ ...prev, [key]: checked }));
  };

  const areMandatoryConsentsAccepted = () =>
    STATIC_CONSENTS.filter((c) => c.isMandatory).every((c) => consentStates[c.key] === true);

  /* ================= UI ================= */

  return (
   <div className="pan-validation-page">
  {!embedded && <OtherPageHeader />}

  <div className="container w-100">
    <Card className="shadow-sm mb-5">
      
      {/* PAGE TITLE (same as LoanForm) */}
      {/* <div className="text-center mb-5">
       */}
       <div className="step-header text-center">
        <h2>Email Verification</h2>
         {/* <p>Complete the highlighted step to proceed</p> */}
      </div>

      <Form
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
        className="pan-form"
      >
        <div className="form-section">
          
          {/* SECTION HEADER (THIS WAS MISSING) */}
          <h3 className="section-title">
            Email Verification Details
          </h3>

          <p className="text-muted mb-4">
            {otpSent
              ? "Enter the 4-digit OTP sent to your email"
              : "Verify your email to continue loan application"}
          </p>

          <Row gutter={[24, 16]}>
            {!otpSent ? (
              <>
                {/* EMAIL FIELD */}
                <Col xs={24}>
                  <Form.Item
                    label="Email Address"
                    validateStatus={errors.email ? "error" : ""}
                    help={errors.email?.message}
                    required
                  >
                    <Controller
                      name="email"
                      control={control}
                      rules={{
                        required: "Email is required",
                        pattern: {
                          value:
                            /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: "Invalid email address",
                        },
                      }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          placeholder="Enter your email"
                          disabled={loading}
                        />
                      )}
                    />
                  </Form.Item>
                  
                  {/* Static Consents */}
                  <div className="mb-3">
                    {STATIC_CONSENTS.map((consent) => (
                      <div key={consent.key} style={{ marginBottom: "10px" }}>
                        <label style={{ display: "flex", alignItems: "flex-start", gap: "8px", cursor: "pointer", margin: 0 }}>
                          <input
                            type="checkbox"
                            checked={consentStates[consent.key] || false}
                            onChange={(e) => handleConsentChange(consent.key, e.target.checked)}
                            style={{ marginTop: "3px", flexShrink: 0, width: "14px", height: "14px", cursor: "pointer" }}
                          />
                          <span style={{ fontSize: "13px", lineHeight: "1.4", textAlign: "left" }}>
                            {consent.message}
                            {consent.isMandatory && (
                              <span style={{ color: "red", marginLeft: "4px" }}>* (Required)</span>
                            )}
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                  
                  {showConsentMsg && (
                    <div style={{ marginTop: "10px", fontSize: "14px", color: "red", textAlign: "center" }}>
                      Please agree to the consent terms to proceed.
                    </div>
                  )}
                </Col>

                {/* EMPTY COLUMN TO MATCH LOAN FORM GRID */}
                <Col xs={24} md={12} />

                {/* BUTTON */}
                <Col span={24} className="text-center mt-4">
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    className="submit-btn"
                    loading={loading}
                    onClick={(e) => {
                      if (!areMandatoryConsentsAccepted()) {
                        e.preventDefault();
                        setShowConsentMsg(true);
                        setTimeout(() => setShowConsentMsg(false), 3000);
                      }
                    }}
                  >
                    Send OTP
                  </Button>
                </Col>
              </>
            ) : (
              <>
                {/* OTP INPUTS */}
                <Col span={24}>
  <div className="otp-container">
    {otp.map((digit, index) => (
      <Input
        key={index}
        ref={(el) => (otpInputRefs.current[index] = el)}
        value={digit}
        maxLength={1}
        onChange={(e) => handleOtpChange(index, e.target.value)}
        onKeyDown={(e) => handleKeyDown(index, e)}
        onPaste={index === 0 ? handleOtpPaste : undefined}
        disabled={loading}
        className="otp-input"
        placeholder="0"
      />
    ))}
  </div>
</Col>

                {/* EMAIL DISPLAY AND ACTIONS */}
                <Col span={24} className="text-center mt-3">
                  <p className="text-muted">
                    OTP sent to: <strong>{currentEmail}</strong>
                  </p>
                  <div className="otp-actions">
                    <Button 
                      type="link" 
                      onClick={() => setOtpSent(false)}
                      className="change-email-btn"
                    >
                      Change Email
                    </Button>
                    <span className="mx-2">|</span>
                    <Button 
                      type="link" 
                      onClick={handleResendOtp}
                      disabled={resendTimer > 0 || loading}
                      className="resend-otp-btn"
                    >
                      {resendTimer > 0 
                        ? `Resend OTP in ${resendTimer}s` 
                        : "Resend OTP"
                      }
                    </Button>
                  </div>
                </Col>

                {/* VERIFY BUTTON */}
                <Col span={24} className="text-center mt-4">
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    className="submit-btn"
                    loading={loading}
                    disabled={otp.some(digit => digit === '')}
                  >
                    Verify OTP
                  </Button>
                </Col>
              </>
            )}
          </Row>
        </div>
      </Form>
    </Card>
  </div>
</div>

  );
}
